import { intersection } from 'lodash';
import { useCallback, useMemo } from 'react';

import { useApplicationConfig } from '~/data/applicationConfig';
import { ProposalType } from '~/data/proposals';
import {
  GetProposalPreviewProps,
  UseGetProposalPreviewOutput,
  useGetProposalPreview,
} from '~/data/proposals/useGetProposalPreview';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';

import {
  EmployeeProposalsTab,
  EmployeeProposalsTabs,
  EmployeeRewardingProposalDialogTabs,
} from './data';

export type UseGetProposalsTabsOutput = {
  isLoading: boolean;
  proposalsTabs: EmployeeProposalsTab[];
  defaultProposalsTab?: EmployeeProposalsTab;
  proposalHistoryTabs: EmployeeProposalsTab[];
  defaultProposalHistoryTab?: EmployeeProposalsTab;
  newWorkflowsEngineEnabled: boolean;
};

type PropsType = {
  enabledDialogTabs: EmployeeRewardingProposalDialogTabs[];
};

export const useGetProposalsTabs = (
  props: PropsType,
): UseGetProposalsTabsOutput => {
  const { enabledDialogTabs } = props;
  const { selectedEmployeeId } = useSelectedEmployeeId();

  const { applicationConfig } = useApplicationConfig();
  const { newWorkflowsEngineEnabled } = useMemo(() => {
    return {
      newWorkflowsEngineEnabled:
        applicationConfig?.fComConfig?.miscellaneousConfig
          ?.fComEnableNewWorkflowsEngine,
    };
  }, [applicationConfig]);

  const previewProps = useCallback(
    (proposalType: ProposalType): GetProposalPreviewProps => {
      return {
        targetId: selectedEmployeeId,
        proposalType,
      };
    },
    [selectedEmployeeId],
  );

  const salaryProposalPreviewOutput = useGetProposalPreview(
    previewProps(ProposalType.SALARY_PROPOSAL),
  );
  const bonusProposalPreviewOutput = useGetProposalPreview(
    previewProps(ProposalType.BONUS_PROPOSAL),
  );
  const billableHoursProposalPreviewOutput = useGetProposalPreview(
    previewProps(ProposalType.BILLABLE_HOURS),
  );
  const benefitProposalPreviewOutput = useGetProposalPreview(
    previewProps(ProposalType.BENEFIT_PROPOSAL),
  );
  const allowanceProposalPreviewOutput = useGetProposalPreview(
    previewProps(ProposalType.ALLOWANCE_PROPOSAL),
  );

  const isLoadingInitialData = useCallback(
    (swrOutput: UseGetProposalPreviewOutput): boolean => {
      return swrOutput.isLoading && !swrOutput.error && !swrOutput.data;
    },
    [],
  );

  const hasInitialData = useCallback(
    (swrOutput: UseGetProposalPreviewOutput) => {
      return !swrOutput.error && !!swrOutput.data;
    },
    [],
  );

  const isLoading = useMemo(() => {
    return (
      [
        salaryProposalPreviewOutput,
        bonusProposalPreviewOutput,
        billableHoursProposalPreviewOutput,
        benefitProposalPreviewOutput,
        allowanceProposalPreviewOutput,
      ].filter(isLoadingInitialData).length > 0
    );
  }, [
    isLoadingInitialData,
    salaryProposalPreviewOutput,
    bonusProposalPreviewOutput,
    billableHoursProposalPreviewOutput,
    benefitProposalPreviewOutput,
    allowanceProposalPreviewOutput,
  ]);

  const getProposalDataByKey = useCallback(
    (key: EmployeeProposalsTabs): EmployeeProposalsTab => {
      switch (key) {
        case EmployeeProposalsTabs.SalaryProposal:
          return {
            name: EmployeeProposalsTabs.SalaryProposal,
            hasWorkflowConfigured: hasInitialData(salaryProposalPreviewOutput),
            available: enabledDialogTabs.includes(
              EmployeeRewardingProposalDialogTabs.General,
            ),
          };
        case EmployeeProposalsTabs.ShortTermIncentivesProposal:
          return {
            name: EmployeeProposalsTabs.ShortTermIncentivesProposal,
            hasWorkflowConfigured: hasInitialData(bonusProposalPreviewOutput),
            available: enabledDialogTabs.includes(
              EmployeeRewardingProposalDialogTabs.Incentives,
            ),
          };
        case EmployeeProposalsTabs.BillableHoursProposal:
          return {
            name: EmployeeProposalsTabs.BillableHoursProposal,
            hasWorkflowConfigured: hasInitialData(
              billableHoursProposalPreviewOutput,
            ),
            available: enabledDialogTabs.includes(
              EmployeeRewardingProposalDialogTabs.BillableHours,
            ),
          };
        case EmployeeProposalsTabs.BenefitProposal:
          return {
            name: EmployeeProposalsTabs.BenefitProposal,
            hasWorkflowConfigured: hasInitialData(benefitProposalPreviewOutput),
            available: enabledDialogTabs.includes(
              EmployeeRewardingProposalDialogTabs.Benefits,
            ),
          };
        case EmployeeProposalsTabs.AllowanceProposal:
          return {
            name: EmployeeProposalsTabs.AllowanceProposal,
            hasWorkflowConfigured: hasInitialData(
              allowanceProposalPreviewOutput,
            ),
            available: enabledDialogTabs.includes(
              EmployeeRewardingProposalDialogTabs.Allowances,
            ),
          };
        case EmployeeProposalsTabs.ProposalsHistory:
        default: {
          return {
            name: EmployeeProposalsTabs.ProposalsHistory,
            available:
              intersection(
                [
                  EmployeeRewardingProposalDialogTabs.General,
                  EmployeeRewardingProposalDialogTabs.Benefits,
                  EmployeeRewardingProposalDialogTabs.Allowances,
                ],
                enabledDialogTabs,
              ).length > 0,
          };
        }
      }
    },
    [
      hasInitialData,
      salaryProposalPreviewOutput,
      bonusProposalPreviewOutput,
      billableHoursProposalPreviewOutput,
      benefitProposalPreviewOutput,
      allowanceProposalPreviewOutput,
      enabledDialogTabs,
    ],
  );

  const returnProps = useMemo(() => {
    const proposalsTabs = Object.keys(EmployeeProposalsTabs).map((key) =>
      getProposalDataByKey(key as EmployeeProposalsTabs),
    );

    const proposalHistoryTabs = proposalsTabs.filter((t) =>
      [
        EmployeeProposalsTabs.SalaryProposal,
        EmployeeProposalsTabs.BenefitProposal,
        EmployeeProposalsTabs.AllowanceProposal,
      ].includes(t.name),
    );

    const getDefault = (tabs: EmployeeProposalsTab[]) =>
      tabs.find((t) => t.available);

    return {
      proposalsTabs,
      defaultProposalsTab: getDefault(proposalsTabs),
      proposalHistoryTabs,
      defaultProposalHistoryTab: getDefault(proposalHistoryTabs),
    };
  }, [getProposalDataByKey]);

  return {
    isLoading,
    ...returnProps,
    newWorkflowsEngineEnabled,
  };
};
